import * as Sentry from '@sentry/browser'
import { Device, Call } from '@twilio/voice-sdk'
import useSnackbar from '@/composables/useSnackbar'
import { usePermissions } from '@/composables/usePermissions'
import { Service } from '@/services/helpers/ServiceHelper'
import { useAudioSources } from '@/composables/useAudioSources'

export function useCallDevice () {
  const snackbar = useSnackbar()
  const callStore = useCallStore()
  const getToken = () => Service.$axiosInstance.$get('/api/sms-threads/token')

  const permissions = usePermissions({
    showExplanationDialog: true,
    onDenied: () => {
      Sentry.captureMessage('Microphone permission denied by user')
    }
  })

  const callDevice = shallowRef<Device | null>(null)
  // shallowRef: updates require reference replacement (`activeCalls.value = new Map(...)`).
  // Mutations via .set/.delete/.clear will NOT trigger reactivity.
  const activeCalls = shallowRef(new Map<string, Call>())

  const ringtoneAudio = ref<HTMLAudioElement | null>(null)

  const startRingtone = () => {
    if (ringtoneAudio.value) { return }
    const audio = new Audio('/sounds/incoming-call-notification.mp3')
    audio.loop = true
    audio.play().catch(() => {})
    ringtoneAudio.value = audio
  }

  const stopRingtone = () => {
    if (!ringtoneAudio.value) { return }
    ringtoneAudio.value.pause()
    ringtoneAudio.value.currentTime = 0
    ringtoneAudio.value = null
  }

  watch(
    () => callStore.incomingRingingCallId,
    (id) => {
      const INITIALIZED_CONFERENCE = id?.startsWith('CF')
      if (INITIALIZED_CONFERENCE) { startRingtone() }
    }
  )

  const micPermission = computed(() => {
    const state = permissions.state('microphone')
    return { state } as PermissionStatus
  })

  const audio = useAudioSources(callDevice)

  const requestMicPermission = async () => {
    try {
      const result = await permissions.request('microphone')

      if (!result.isGranted) {
        return false
      }

      if (!audio.inputSource.value || !audio.outputSource.value) {
        await audio.setAudioSources()
      }
      return true
    } catch (error) {
      Sentry.captureException(error)
      return false
    }
  }

  const removeDevice = async () => {
    try {
      await callDevice.value?.unregister()
      callDevice.value?.removeAllListeners()
      audio.removeAudioSources()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to remove call device')
    } finally {
      activeCalls.value = new Map<string, Call>()
      callDevice.value = null
    }
  }

  const removeActiveCall = (sid: string) => {
    const updated = new Map(activeCalls.value)
    updated.delete(sid)
    activeCalls.value = updated
    stopRingtone()
  }

  const trackCall = (call: Call) => {
    const sid = call.parameters.CallSid
    if (sid) {
      const updated = new Map(activeCalls.value)
      updated.set(sid, call)
      activeCalls.value = updated
      callStore.addRingingCallEntry(call)
      call.on('accept', () => {
        callStore.acceptRingingCall()
        stopRingtone()
      })
      call.on('disconnect', () => removeActiveCall(sid))
      call.on('cancel', () => removeActiveCall(sid))
      call.on('reject', () => removeActiveCall(sid))
    }
  }

  const getCallBySid = (callSid: string): Call | undefined => {
    return activeCalls.value.get(callSid)
  }

  const setCallDevice = async () => {
    if (callDevice.value) {
      // eslint-disable-next-line no-console
      console.log('>>> Device already exists <<<')
      return
    }
    try {
      const token = await getToken()
      callDevice.value = new Device(token, { allowIncomingWhileBusy: true })
      // Disabling Twilio's incoming ringtone
      callDevice.value?.audio?.incoming(false)
      callDevice.value?.on('error', (error) => {
        Sentry.captureException(error)
        snackbar.error('There was an error with the call device')
        removeDevice()
      })
      callDevice.value?.on('tokenWillExpire', async () => {
        const token = await getToken()
        callDevice.value?.updateToken(token)
      })
      callDevice.value?.on('incoming', (call: Call) => {
        trackCall(call)
      })
      callDevice.value?.on('registered', () => {
        // eslint-disable-next-line no-console
        console.log('>>> DEVICE REGISTERED SUCCESSFULLY <<<')
      })
      callDevice.value?.on('unregistered', () => {
        // eslint-disable-next-line no-console
        console.log('>>> DEVICE UNREGISTERED <<<')
      })
      await callDevice.value?.register()
    } catch (error) {
      Sentry.captureException(error)
      snackbar.error('Failed to get device')
      removeDevice()
    }
  }

  return {
    callDevice,
    micPermission,
    requestMicPermission,
    setCallDevice,
    removeDevice,
    getCallBySid,
    stopRingtone,
    ...audio
  }
}
